import type { TaskStatusOutput } from "@orsane/schema/tasks";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { statusColors } from "@/app/colors";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ActionsProps {
    status: TaskStatusOutput;
    onEdit: (status: TaskStatusOutput) => void;
    onDelete: (id: number) => void;
}

const ActionsCell = ({ status, onEdit, onDelete }: ActionsProps) => {
    const canDelete = !status.isSystem;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                }
            ></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(status)}>
                    <Pencil />
                    Edit
                </DropdownMenuItem>
                {canDelete && (
                    <DropdownMenuItem onClick={() => onDelete(status.id)}>
                        <Trash2 />
                        Delete
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export const createTaskStatusColumns = (
    onEdit: (status: TaskStatusOutput) => void,
    onDelete: (id: number) => void
): ColumnDef<TaskStatusOutput>[] => [
    // Note: The drag handle column is added separately in the table, not here
    {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
            const status = row.original;
            const color = status.color ? statusColors[status.color as keyof typeof statusColors] : null;
            return (
                <div className="font-medium flex items-center gap-2">
                    {status.isDefault && <div className={`w-3 h-3 rounded-full bg-gray-300`} />}
                    {status.isCompleted && <div className={`w-3 h-3 rounded-full bg-emerald-400`} />}
                    {color && <div className={`w-3 h-3 rounded-full ${color?.darkerClassName}`} />}
                    <span>{status.name}</span>
                    {status.isDefault && <span className="text-xs text-muted-foreground">(Default)</span>}
                    {status.isCompleted && <span className="text-xs text-muted-foreground">(Completed)</span>}
                </div>
            );
        },
    },
    {
        id: "actions",
        enableHiding: false,
        meta: {
            align: "right",
            width: 100,
        },
        cell: ({ row }) => {
            return <ActionsCell status={row.original} onEdit={onEdit} onDelete={onDelete} />;
        },
    },
];
